import { Box } from '@zoopla-eng/zds/box';
import {
    DetachedHouseMedium,
    FlatsMedium,
    SemiDetachedHouseMedium,
    TerracedHouseMedium
} from '@zoopla-eng/zds/icons';
import { fontGroups } from '@zoopla-eng/zds/themes';
import { formatter as currencyFormatter } from '@zoopla/shared/currency';
import { FC } from 'react';
import { MarketActivityFragment } from '../MarketActivity/MarketActivity.fragment.generated';
import {
    differentPropertyListItemStyle,
    differentPropertyListStyle,
    differentPropertyListTitleStyle,
    styledDdStyle
} from './index.styles.css';

type PropertyTypeStats = MarketActivityFragment | null | undefined;

type SeoBlurbProps = {
    areaName?: string | null | undefined;
    seoBlurbStats: {
        all?: PropertyTypeStats;
        detached?: PropertyTypeStats;
        semi?: PropertyTypeStats;
        terraced?: PropertyTypeStats;
        flat?: PropertyTypeStats;
    };
};

const getCurrencyValue = (price: number | null | undefined) => {
    if (!price) {
        return 'N/A';
    }

    return currencyFormatter.format(price);
};

export const SeoBlurb: FC<React.PropsWithChildren<SeoBlurbProps>> = ({
    areaName,
    seoBlurbStats
}) => {
    const { all, detached, semi, flat, terraced } = seoBlurbStats;

    const detachedAveragePricePaid = detached?.data?.averagePricePaid;
    const semiAveragePricePaid = semi?.data?.averagePricePaid;
    const terracedAveragePricePaid = terraced?.data?.averagePricePaid;
    const flatAveragePricePaid = flat?.data?.averagePricePaid;

    const hasFullData =
        detachedAveragePricePaid &&
        semiAveragePricePaid &&
        terracedAveragePricePaid &&
        flatAveragePricePaid;

    const averageSoldText = `The average sold price for a property in ${areaName} in the last
    12 months is ${getCurrencyValue(all?.data?.averagePricePaid)}.`;

    if (!hasFullData) {
        return (
            <aside className={fontGroups.ladle} aria-label="seo-blurb">
                <Box pt="S200" pb="S500">
                    <p>{averageSoldText}</p>
                </Box>
            </aside>
        );
    }

    const differentPropertyTypesText = `Different property types in ${areaName} have different average
    sold prices over the last 12 months:`;

    const detachedText = getCurrencyValue(detachedAveragePricePaid);
    const semiText = getCurrencyValue(semiAveragePricePaid);
    const terracedText = getCurrencyValue(terracedAveragePricePaid);
    const flatsText = getCurrencyValue(flatAveragePricePaid);

    const propertyTypeInfo: [string, string, JSX.Element][] = [
        ['Detached', detachedText, <DetachedHouseMedium />],
        ['Semi-detached', semiText, <SemiDetachedHouseMedium />],
        ['Terraced', terracedText, <TerracedHouseMedium />],
        ['Flats', flatsText, <FlatsMedium />]
    ];

    return (
        <aside className={fontGroups.ladle} aria-label="seo-blurb">
            <Box pt="S200" pb="S500">
                <p>{averageSoldText}</p>
                <p>{differentPropertyTypesText}</p>
            </Box>
            <div className={differentPropertyListStyle}>
                {propertyTypeInfo.map(([label, text, icon]) => (
                    <Box display="flex" key={label}>
                        <Box
                            p="S300"
                            pl={0}
                            display="none"
                            bpS200={{ display: 'inline-block' }}
                        >
                            <div aria-hidden="true">{icon}</div>
                        </Box>
                        <dl
                            className={differentPropertyListItemStyle}
                            key={label}
                        >
                            <dt className={differentPropertyListTitleStyle}>
                                {`${label} `}
                            </dt>
                            <dd className={styledDdStyle}>{text}</dd>
                        </dl>
                    </Box>
                ))}
            </div>
        </aside>
    );
};
